import * as C from "../constants/jewelryHireConstants";
import api from "../utils/axiosInstance";
import { getApiErrorMessage } from "../utils/errorMessages";

const safeText = (value = "") => String(value ?? "").trim();

const getErrorMessage = (error, fallback) =>
  getApiErrorMessage(error, { fallback });

const normalizeImages = (images = []) =>
  (Array.isArray(images) ? images : [])
    .map((image) => ({
      url: safeText(image?.url || image?.secure_url),
      public_id: safeText(image?.public_id),
      alt: safeText(image?.alt),
      caption: safeText(image?.caption),
    }))
    .filter((image) => image.url);

const toPositiveInteger = (value, fallback) => {
  const number = Math.floor(Number(value));
  return Number.isFinite(number) && number > 0 ? number : fallback;
};

const addQueryValue = (params, key, value) => {
  const safeValue = safeText(value);
  if (safeValue) params.set(key, safeValue);
};

function buildListQuery(filters = {}, admin = false) {
  const params = new URLSearchParams();

  addQueryValue(params, "category", filters.category);
  addQueryValue(params, "subcategory", filters.subcategory);
  addQueryValue(params, "keyword", filters.keyword);
  params.set("pageNumber", String(toPositiveInteger(filters.pageNumber, 1)));
  params.set("pageSize", String(toPositiveInteger(filters.pageSize, 24)));

  if (admin && typeof filters.isPublished === "boolean") {
    params.set("isPublished", String(filters.isPublished));
  }

  return params.toString();
}

function normalizeItemPayload(item = {}) {
  return {
    name: safeText(item.name),
    slug: safeText(item.slug),
    description: safeText(item.description),
    category: safeText(item.category),
    subcategory: safeText(item.subcategory),
    images: normalizeImages(item.images),
    specifications: (Array.isArray(item.specifications)
      ? item.specifications
      : []
    )
      .map(safeText)
      .filter(Boolean),
    displayOrder: Math.max(0, Math.floor(Number(item.displayOrder) || 0)),
    isPublished: item.isPublished !== false,
  };
}

export const resetJewelryHireList = () => ({
  type: C.JEWELRY_HIRE_LIST_RESET,
});

export const resetJewelryHireDetails = () => ({
  type: C.JEWELRY_HIRE_DETAILS_RESET,
});

export const resetAdminJewelryHireDetails = () => ({
  type: C.ADMIN_JEWELRY_HIRE_DETAILS_RESET,
});

export const resetAdminJewelryHireCreate = () => ({
  type: C.ADMIN_JEWELRY_HIRE_CREATE_RESET,
});

export const resetAdminJewelryHireUpdate = () => ({
  type: C.ADMIN_JEWELRY_HIRE_UPDATE_RESET,
});

export const resetAdminJewelryHireDelete = () => ({
  type: C.ADMIN_JEWELRY_HIRE_DELETE_RESET,
});

export const listJewelryHireItems =
  (filters = {}) => async (dispatch) => {
    try {
      dispatch({ type: C.JEWELRY_HIRE_LIST_REQUEST, meta: filters });

      const query = buildListQuery(filters);
      const { data } = await api.get(`/jewelry-hire?${query}`);

      dispatch({
        type: C.JEWELRY_HIRE_LIST_SUCCESS,
        payload: data,
        meta: filters,
      });

      return data;
    } catch (error) {
      dispatch({
        type: C.JEWELRY_HIRE_LIST_FAIL,
        payload: getErrorMessage(
          error,
          "We could not load the jewellery hire collection right now.",
        ),
      });
      throw error;
    }
  };

export const getJewelryHireItem = (identifier) => async (dispatch) => {
  const safeIdentifier = safeText(identifier);

  try {
    dispatch({ type: C.JEWELRY_HIRE_DETAILS_REQUEST });

    if (!safeIdentifier) throw new Error("Item identifier is required.");

    const { data } = await api.get(
      `/jewelry-hire/${encodeURIComponent(safeIdentifier)}`,
    );

    dispatch({ type: C.JEWELRY_HIRE_DETAILS_SUCCESS, payload: data });
    return data;
  } catch (error) {
    dispatch({
      type: C.JEWELRY_HIRE_DETAILS_FAIL,
      payload: getErrorMessage(
        error,
        "We could not load this jewellery hire item right now.",
      ),
    });
    throw error;
  }
};

export const listJewelryHireItemsAdmin =
  (filters = {}) => async (dispatch) => {
    try {
      dispatch({ type: C.ADMIN_JEWELRY_HIRE_LIST_REQUEST, meta: filters });

      const query = buildListQuery(filters, true);
      const { data } = await api.get(`/jewelry-hire/admin/list?${query}`);

      dispatch({
        type: C.ADMIN_JEWELRY_HIRE_LIST_SUCCESS,
        payload: data,
        meta: filters,
      });

      return data;
    } catch (error) {
      dispatch({
        type: C.ADMIN_JEWELRY_HIRE_LIST_FAIL,
        payload: getErrorMessage(
          error,
          "We could not load jewellery hire items right now.",
        ),
      });
      throw error;
    }
  };

export const getJewelryHireItemAdmin = (id) => async (dispatch) => {
  try {
    dispatch({ type: C.ADMIN_JEWELRY_HIRE_DETAILS_REQUEST });
    const { data } = await api.get(
      `/jewelry-hire/admin/${encodeURIComponent(safeText(id))}`,
    );
    dispatch({ type: C.ADMIN_JEWELRY_HIRE_DETAILS_SUCCESS, payload: data });
    return data;
  } catch (error) {
    dispatch({
      type: C.ADMIN_JEWELRY_HIRE_DETAILS_FAIL,
      payload: getErrorMessage(error, "We could not load this item right now."),
    });
    throw error;
  }
};

export const createJewelryHireItemAdmin = (item) => async (dispatch) => {
  try {
    dispatch({ type: C.ADMIN_JEWELRY_HIRE_CREATE_REQUEST });
    const { data } = await api.post(
      "/jewelry-hire/admin",
      normalizeItemPayload(item),
    );
    dispatch({ type: C.ADMIN_JEWELRY_HIRE_CREATE_SUCCESS, payload: data });
    return data;
  } catch (error) {
    dispatch({
      type: C.ADMIN_JEWELRY_HIRE_CREATE_FAIL,
      payload: getErrorMessage(error, "We could not create this item."),
    });
    throw error;
  }
};

export const updateJewelryHireItemAdmin =
  (id, item) => async (dispatch) => {
    try {
      dispatch({ type: C.ADMIN_JEWELRY_HIRE_UPDATE_REQUEST });
      const { data } = await api.put(
        `/jewelry-hire/admin/${encodeURIComponent(safeText(id))}`,
        normalizeItemPayload(item),
      );
      dispatch({ type: C.ADMIN_JEWELRY_HIRE_UPDATE_SUCCESS, payload: data });
      return data;
    } catch (error) {
      dispatch({
        type: C.ADMIN_JEWELRY_HIRE_UPDATE_FAIL,
        payload: getErrorMessage(error, "We could not update this item."),
      });
      throw error;
    }
  };

export const deleteJewelryHireItemAdmin = (id) => async (dispatch) => {
  try {
    dispatch({ type: C.ADMIN_JEWELRY_HIRE_DELETE_REQUEST });
    const { data } = await api.delete(
      `/jewelry-hire/admin/${encodeURIComponent(safeText(id))}`,
    );
    dispatch({ type: C.ADMIN_JEWELRY_HIRE_DELETE_SUCCESS, payload: data });
    return data;
  } catch (error) {
    dispatch({
      type: C.ADMIN_JEWELRY_HIRE_DELETE_FAIL,
      payload: getErrorMessage(error, "We could not delete this item."),
    });
    throw error;
  }
};
